import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * hallmark critique · DemandSimulateButton
 * tokens locked from docs/design.md §5 (Filled Dark Pill) + §6 (single clay)
 * states covered: idle · hover · focus · active · disabled · loading · empty · error
 *                 (`sending` maps to loading · `error` shows a retry copy)
 * mobile verified: 320 / 375 / 414 / 768
 * no fake chrome · no italic headers · no shadows · single clay accent per viewport
 *
 * A filled-dark pill button used inside the PrivacyPanel to POST a random
 * mock MCP `buses_near_stop` call to `/api/mcp/simulate`. Once the fetch
 * settles it triggers `onSimulated()` (typically `refetch()` on the
 * heatmap hook) so the heatmap repopulates without waiting on the 15s
 * poll. Randomization is intentionally BOUNDED — stop_ids from AS-4
 * S01..S07 + a caller inside a 3km ring around Andheri — so demo output
 * lands where the operator's map is centered.
 *
 * The button is dark-on-white; the clay accent for the viewport stays
 * owned by the heatmap layer + Demand tab's active nav pill.
 */
import { useCallback, useRef, useState } from "react";
import { PillButton } from "../common/PillButton";
import { getBaseUrl } from "../../lib/api";
import { AS_4_STOPS, MUMBAI_CENTER } from "../../lib/tokens";
const captionStyle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "var(--color-cool-ash)",
    marginTop: 8,
};
/**
 * Return a caller lat/lng inside a ~3km ring around Andheri
 * (19.1090, 72.8712) using the deterministic-ish `Math.random` seeded
 * only implicitly by wall clock — we don't need repeatability, just
 * spatial bounding.
 */
function randomCallerAroundAndheri() {
    // Andheri ~= MUMBAI_CENTER; use it as ring centre for the demo.
    const [clat, clng] = MUMBAI_CENTER;
    // 3km ring → 1km inner floor so calls never sit exactly on the centre.
    const rMin = 0.5;
    const rMax = 3.0;
    const r = rMin + Math.random() * (rMax - rMin);
    const theta = Math.random() * Math.PI * 2;
    // 1 deg latitude ≈ 111 km; adjust longitude by cos(lat).
    const dLat = (r / 111) * Math.cos(theta);
    const dLng = (r / (111 * Math.cos((clat * Math.PI) / 180))) * Math.sin(theta);
    return { lat: clat + dLat, lng: clng + dLng };
}
function randomStopId() {
    return AS_4_STOPS[Math.floor(Math.random() * AS_4_STOPS.length)].stopId;
}
function randomRadiusM() {
    // Bounded [300, 1200] to keep synthetic results interesting.
    return 300 + Math.floor(Math.random() * 900);
}
function randomConsentToken() {
    // 12-char alphanumeric — meets the min-length-8 server rule.
    const alpha = "abcdefghijklmnopqrstuvwxyz0123456789";
    let s = "demo-";
    for (let i = 0; i < 8; i++) {
        s += alpha[Math.floor(Math.random() * alpha.length)];
    }
    return s;
}
export function buildSimulateRequest() {
    const caller = randomCallerAroundAndheri();
    return {
        stop_id: randomStopId(),
        radius_m: randomRadiusM(),
        caller_lat: caller.lat,
        caller_lng: caller.lng,
        consent_token: randomConsentToken(),
    };
}
async function postSimulate(base, req, signal) {
    const url = `${base.replace(/\/+$/, "")}/api/mcp/simulate`;
    const res = await fetch(url, {
        method: "POST",
        signal,
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        body: JSON.stringify(req),
    });
    if (!res.ok) {
        let body = null;
        try {
            body = await res.json();
        }
        catch {
            /* ignore */
        }
        const err = new Error(`POST /api/mcp/simulate failed with HTTP ${res.status}${body ? ` · ${JSON.stringify(body)}` : ""}`);
        err.status = res.status;
        throw err;
    }
    return (await res.json());
}
function copyFor(state) {
    switch (state) {
        case "sending":
            return "Simulating…";
        case "sent":
            return "Call recorded";
        case "error":
            return "Retry simulate";
        case "disabled":
            return "Simulate call";
        case "idle":
        default:
            return "Simulate call";
    }
}
export function DemandSimulateButton({ onSimulated, forcedState, disabled = false, baseUrl, onRequest, }) {
    const [state, setState] = useState("idle");
    const [lastError, setLastError] = useState(null);
    const [lastResponse, setLastResponse] = useState(null);
    const abortRef = useRef(null);
    const effectiveState = forcedState ?? state;
    const isSending = effectiveState === "sending";
    const isDisabled = disabled || effectiveState === "disabled" || isSending;
    const onClick = useCallback(async () => {
        if (isDisabled || forcedState)
            return;
        setLastError(null);
        setState("sending");
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        const req = buildSimulateRequest();
        onRequest?.(req);
        const base = baseUrl ?? getBaseUrl();
        try {
            const res = await postSimulate(base, req, ac.signal);
            setLastResponse(res);
            setState("sent");
            if (onSimulated) {
                await onSimulated(res);
            }
            // Return to idle after a short beat so the pill can be clicked again.
            window.setTimeout(() => {
                setState((cur) => (cur === "sent" ? "idle" : cur));
            }, 900);
        }
        catch (err) {
            if (err.name === "AbortError")
                return;
            setLastError(err instanceof Error ? err.message : String(err));
            setState("error");
        }
    }, [baseUrl, forcedState, isDisabled, onRequest, onSimulated]);
    return (_jsxs("div", { "data-testid": "demand-simulate", "data-state": effectiveState, style: { display: "flex", flexDirection: "column", alignItems: "stretch" }, children: [_jsxs(PillButton, { variant: "filled", size: "md", onClick: () => void onClick(), disabled: isDisabled, "aria-busy": isSending, "data-testid": "demand-simulate-button", children: [isSending && (_jsx("span", { "aria-hidden": "true", style: {
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "var(--color-pure-white)",
                            opacity: 0.85,
                            animation: "bb-live-pulse 1.4s ease-in-out infinite",
                        } })), copyFor(effectiveState)] }), _jsx("div", { style: captionStyle, "data-testid": "demand-simulate-caption", children: effectiveState === "error"
                    ? lastError
                        ? `Error · ${lastError.slice(0, 80)}`
                        : "Error · retry when ready"
                    : effectiveState === "sent" && lastResponse
                        ? `Recorded · ${lastResponse.buses.length} buses near ${lastResponse.stop_id}`
                        : "POSTs to /api/mcp/simulate — a consented + aggregated demand pulse." })] }));
}
